import State from 'store/State';
import {
    createUpdateWidgetComponentStateAction,
    WidgetSetComponentAction,
    WidgetSetComponentPayload,
} from 'widgets/store/component/actions';
import { Reducer } from 'redux';
import { WidgetType } from 'widgets/WidgetType';
import { WidgetWithComponentState } from 'widgets/store/component/WidgetWithComponentState';
import { useSelector } from 'react-redux';
import { createComponentStateReducer } from 'widgets/store/component/reducer';
import { useDispatchUpdateWidgetComponentState } from 'widgets/store/component/dispatcher';
import { BooleanKeys, OnlyBoolean } from 'utils/types/BooleanKeys';
import { useWidgetContext } from 'widgets/WidgetContext';

export interface ComponentSliceSelectors<TComponentState> {
    /*
     * Selects the component state for a widget
     */
    selectComponentState: (state: State, widgetKey: string) => TComponentState;
    /*
     * Hook to return the component state for a widget
     */
    useComponentSelector: () => TComponentState;
}

export interface ComponentSliceActions<TComponentState> {
    /*
     * Action creator for updating a widget component state
     */
    createUpdateComponentAction: (
        widgetKey: string,
        componentDelta: Partial<TComponentState>
    ) => WidgetSetComponentAction<TComponentState>;
}

export interface UseToggleComponentOptionRet {
    value: boolean;
    toggle: () => void;
}

export interface ComponentSliceHooks<TComponentState> {
    /*
     * Hook to dispatch an update widget component action
     */
    useDispatchUpdateComponentAction: () => (
        componentDelta: Partial<TComponentState>
    ) => void;
    /*
     * Hook to toggle a component option
     */
    useToggleComponentOption: (
        option: BooleanKeys<TComponentState>
    ) => UseToggleComponentOptionRet;
}
/*
 * Slice of state for Widget component
 */
export interface ComponentSlice<TComponentState> {
    selectors: ComponentSliceSelectors<TComponentState>;
    actions: ComponentSliceActions<TComponentState>;
    reducer: Reducer<
        TComponentState,
        WidgetSetComponentAction<TComponentState>
    >;
    hooks: ComponentSliceHooks<TComponentState>;
    widgetType: WidgetType;
}

/*
 * Build a Component slice of state for a widget type
 */
export function buildComponentSlice<TComponentState>(
    widgetType: WidgetType,
    selectWidgetState: (
        state: State,
        widgetKey: string
    ) => WidgetWithComponentState<TComponentState>,
    initialComponentState: TComponentState
): ComponentSlice<TComponentState> {
    // Selectors...

    const selectComponentState = (state: State, widgetKey: string) => {
        const widgetState = selectWidgetState(state, widgetKey);
        return widgetState && widgetState.component
            ? widgetState.component
            : initialComponentState;
    };

    const useComponentSelector = () => {
        const { widgetKey } = useWidgetContext();
        return useSelector((state: State) =>
            selectComponentState(state, widgetKey)
        );
    };

    // Actions...

    const createUpdateComponentAction =
        createUpdateWidgetComponentStateAction<TComponentState>(widgetType);

    // Hooks...

    const useDispatchUpdateComponentAction = () => {
        return useDispatchUpdateWidgetComponentState<TComponentState>();
    };

    const useToggleComponentOption = (
        option: BooleanKeys<TComponentState>
    ): UseToggleComponentOptionRet => {
        const value = !!useComponentSelector()[option];
        const updateComponent = useDispatchUpdateComponentAction();

        const toggle = () => {
            const delta: Partial<OnlyBoolean<TComponentState>> = {};
            delta[option] = !value;
            updateComponent(delta as Partial<TComponentState>);
        };

        return { value, toggle };
    };

    const hooks: ComponentSliceHooks<TComponentState> = {
        useDispatchUpdateComponentAction,
        useToggleComponentOption,
    };

    // Reducer...

    const reducer = createComponentStateReducer<TComponentState>(
        initialComponentState
    );

    return {
        selectors: {
            selectComponentState,
            useComponentSelector,
        },
        actions: {
            createUpdateComponentAction,
        },
        reducer,
        hooks,
        widgetType,
    };
}

export type WidgetComponentSliceActionPayloadType<TComponentState> =
    WidgetSetComponentPayload<TComponentState>;

export function useComponentSlice<TComponentState>(
    componentSlice: ComponentSlice<TComponentState>
) {
    const { selectors, hooks, widgetType } = componentSlice;
    const { widgetKey } = useWidgetContext();
    return {
        widgetType,
        widgetKey,
        component: selectors.useComponentSelector(),
        useToggleComponentOption: (option: BooleanKeys<TComponentState>) =>
            hooks.useToggleComponentOption(option),
        dispatchComponentChanged: hooks.useDispatchUpdateComponentAction(),
    };
}
